// ===================================================================
// SHELTER — Report Generation
// Printable reports and CSV exports for shelter operations
// ===================================================================

import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack,
  SimpleGrid, Table, Divider,
} from '@mantine/core';
import { IconPrinter, IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import {
  SHELTER_STATUS_COLORS, SHELTER_TYPE_LABELS,
  SPECIAL_NEEDS_OPTIONS, INCIDENT_REPORT_TYPES,
} from '../../types/shelter';
import type { ShelterStatus } from '../../types/shelter';
import { formatPhone } from '../../lib/phoneFormat';

type ReportType = 'shelter_status' | 'survivor_roster' | 'capacity' | 'supply_inventory' | 'medical_summary' | 'incident_log';

const REPORT_TYPES: { value: ReportType; label: string; description: string }[] = [
  { value: 'shelter_status', label: 'Shelter Status', description: 'Current status, population, and capacity of all shelters' },
  { value: 'survivor_roster', label: 'Survivor Roster', description: 'Complete list of all checked-in survivors' },
  { value: 'capacity', label: 'Capacity Report', description: 'Capacity utilization across all shelters' },
  { value: 'supply_inventory', label: 'Supply Inventory', description: 'Current supply levels at each shelter' },
  { value: 'medical_summary', label: 'Medical Summary', description: 'Medical log entries, medications, follow-ups' },
  { value: 'incident_log', label: 'Incident Log', description: 'All shelter incidents with severity and resolution' },
];

export default function ShelterReports() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const printRef = useRef<HTMLDivElement>(null);

  const {
    shelters, survivors, supplies, medicalLog, shelterIncidents,
    loadShelters, loadSurvivors, loadSupplies, loadMedicalLog, loadShelterIncidents,
  } = useShelterStore();

  const [reportType, setReportType] = useState<ReportType>('shelter_status');

  useEffect(() => {
    loadShelters(); loadSurvivors(); loadSupplies(); loadMedicalLog(); loadShelterIncidents();
  }, [loadShelters, loadSurvivors, loadSupplies, loadMedicalLog, loadShelterIncidents]);

  // Store already server-side filters by active incident; use directly
  const iShelters = incidentId ? shelters : [];
  const iSurvivors = incidentId ? survivors : [];
  const iSupplies = incidentId ? supplies : [];
  const iMedical = incidentId ? medicalLog : [];
  const iIncidents = incidentId ? shelterIncidents : [];

  const shelterName = (id: string) => shelters.find(s => String(s.id) === id)?.name ?? id;
  const survivorFullName = (id: string) => {
    const s = survivors.find(sv => String(sv.id) === id);
    return s ? `${s.firstName} ${s.lastName}` : id;
  };

  const totalPopulation = iShelters.reduce((s, sh) => s + sh.currentPopulation, 0);
  const totalCapacity = iShelters.reduce((s, sh) => s + sh.capacity, 0);
  const checkedIn = iSurvivors.filter(s => s.status === 'checked_in').length;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const getSpecialNeedLabel = (val: string) =>
    SPECIAL_NEEDS_OPTIONS.find(o => o.value === val)?.label ?? val;

  const reportTypeLabel = (val: string) => INCIDENT_REPORT_TYPES.find(t => t.value === val)?.label ?? val;

  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html><head><title>Shelter Report - ${REPORT_TYPES.find(r => r.value === reportType)?.label}</title>
          <style>body{font-family:Arial,sans-serif;padding:24px;font-size:12px}
          table{width:100%;border-collapse:collapse;margin:12px 0}
          th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
          th{background:#f5f5f5;font-weight:700}
          h1{font-size:18px}h2{font-size:14px;margin-top:16px}
          .header{border-bottom:2px solid #333;padding-bottom:8px;margin-bottom:16px}
          .summary{background:#f9f9f9;padding:12px;border-radius:4px;margin:12px 0}
          @media print{body{padding:0}}</style>
          </head><body>${printRef.current.innerHTML}</body></html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    }
  };

  const exportCSV = () => {
    let csv = '';
    if (reportType === 'shelter_status') {
      csv = 'Name,Address,Type,Status,Population,Capacity,Utilization,ADA,PetFriendly,Org\n';
      iShelters.forEach(s => {
        const pct = s.capacity > 0 ? ((s.currentPopulation / s.capacity) * 100).toFixed(1) : '0';
        csv += `"${s.name}","${s.address}","${SHELTER_TYPE_LABELS[s.shelterType] ?? s.shelterType}","${s.status}",${s.currentPopulation},${s.capacity},${pct}%,"${s.isAda}","${s.petFriendly}","${s.operatingOrg}"\n`;
      });
    } else if (reportType === 'survivor_roster') {
      csv = 'FirstName,LastName,Status,Shelter,Cot,Phone,Email,SpecialNeeds,IntakeDate\n';
      iSurvivors.filter(s => s.status === 'checked_in').forEach(s => {
        let needs = '';
        try { needs = JSON.parse(s.specialNeeds).map(getSpecialNeedLabel).join('; '); } catch { /* skip */ }
        csv += `"${s.firstName}","${s.lastName}","${s.status}","${s.shelterId ? shelterName(s.shelterId) : ''}","${s.cotAssignment}","${s.phone}","${s.email}","${needs}","${s.intakeTimestamp}"\n`;
      });
    } else if (reportType === 'capacity') {
      csv = 'Shelter,Population,Capacity,Utilization,Status\n';
      iShelters.forEach(s => {
        const pct = s.capacity > 0 ? ((s.currentPopulation / s.capacity) * 100).toFixed(1) : '0';
        csv += `"${s.name}",${s.currentPopulation},${s.capacity},${pct}%,"${s.status}"\n`;
      });
    } else if (reportType === 'supply_inventory') {
      csv = 'Shelter,Item,Category,Quantity,Unit,ReorderThreshold,LastRestocked\n';
      iSupplies.forEach(s => {
        csv += `"${s.shelterId ? shelterName(s.shelterId) : ''}","${s.itemName}","${s.category}",${s.quantity},"${s.unit}",${s.reorderThreshold},"${s.lastRestocked}"\n`;
      });
    } else if (reportType === 'medical_summary') {
      csv = 'Survivor,Shelter,Type,Description,Medication,Dosage,AdministeredBy,Date,FollowUp\n';
      iMedical.forEach(m => {
        csv += `"${m.survivorId ? survivorFullName(m.survivorId) : ''}","${m.shelterId ? shelterName(m.shelterId) : ''}","${m.entryType}","${m.description}","${m.medicationName}","${m.dosage}","${m.administeredBy}","${m.timestamp}","${m.followUpNeeded}"\n`;
      });
    } else if (reportType === 'incident_log') {
      csv = 'Shelter,Type,Severity,Description,ReportedBy,Date,Resolved,Resolution\n';
      iIncidents.forEach(i => {
        csv += `"${i.shelterId ? shelterName(i.shelterId) : ''}","${i.reportType}","${i.severity}","${i.description}","${i.reportedBy}","${i.reportedAt}","${i.resolvedAt ? 'Yes' : 'No'}","${i.resolution}"\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shelter-${reportType}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notifications.show({ message: 'CSV exported', color: 'green' });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Shelter Reports</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCSV}>Export CSV</Button>
          <Button size="xs" variant="light" leftSection={<IconPrinter size={14} />} onClick={handlePrint}>Print</Button>
        </Group>
      </Group>

      {/* Report type selector */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
        {REPORT_TYPES.map(rt => (
          <Paper key={rt.value} p="sm" radius="md" withBorder
            style={{ cursor: 'pointer', border: reportType === rt.value ? '2px solid var(--color-primary-700)' : undefined }}
            onClick={() => setReportType(rt.value)}>
            <Text size="sm" fw={600}>{rt.label}</Text>
            <Text size="xs" c="dimmed">{rt.description}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Report content */}
      <Paper p="lg" radius="md" withBorder ref={printRef}>
        <div className="header">
          <Text size="lg" fw={800}>OpenGov EMC — Shelter Management</Text>
          <Text size="sm" fw={600}>{REPORT_TYPES.find(r => r.value === reportType)?.label}</Text>
          <Text size="xs" c="dimmed">
            Incident: {activeIncident?.name ?? 'All'} &middot; Generated: {today}
          </Text>
        </div>

        {/* Summary box */}
        <div style={{ background: 'var(--mantine-color-gray-0)', padding: 12, borderRadius: 4, marginBottom: 16 }}>
          <SimpleGrid cols={4}>
            <div><Text size="xs" fw={700}>Shelters</Text><Text fw={600}>{iShelters.length}</Text></div>
            <div><Text size="xs" fw={700}>Total Population</Text><Text fw={600}>{totalPopulation}</Text></div>
            <div><Text size="xs" fw={700}>Total Capacity</Text><Text fw={600}>{totalCapacity}</Text></div>
            <div><Text size="xs" fw={700}>Checked In</Text><Text fw={600}>{checkedIn}</Text></div>
          </SimpleGrid>
        </div>

        {reportType === 'shelter_status' && (
          <>
            <Text fw={700} mb="xs">Shelter Status Overview</Text>
            <div className="report-scroll"><Table striped withTableBorder stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th><Table.Th>Type</Table.Th><Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Population</Table.Th><Table.Th ta="right">Capacity</Table.Th>
                  <Table.Th ta="right">Util %</Table.Th><Table.Th>ADA</Table.Th><Table.Th>Org</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iShelters.map(s => {
                  const pct = s.capacity > 0 ? ((s.currentPopulation / s.capacity) * 100).toFixed(1) : '0';
                  return (
                    <Table.Tr key={s.id}>
                      <Table.Td fw={500}>{s.name}</Table.Td>
                      <Table.Td>{SHELTER_TYPE_LABELS[s.shelterType] ?? s.shelterType}</Table.Td>
                      <Table.Td><Badge size="xs" color={SHELTER_STATUS_COLORS[s.status as ShelterStatus]}>{s.status.replace(/_/g, ' ')}</Badge></Table.Td>
                      <Table.Td ta="right">{s.currentPopulation}</Table.Td>
                      <Table.Td ta="right">{s.capacity}</Table.Td>
                      <Table.Td ta="right">{pct}%</Table.Td>
                      <Table.Td>{s.isAda ? 'Yes' : 'No'}</Table.Td>
                      <Table.Td>{s.operatingOrg || '-'}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table></div>
          </>
        )}

        {reportType === 'survivor_roster' && (
          <>
            <Text fw={700} mb="xs">Survivor Roster (Checked In)</Text>
            <div className="report-scroll"><Table striped withTableBorder stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th><Table.Th>Shelter</Table.Th><Table.Th>Cot</Table.Th>
                  <Table.Th>Phone</Table.Th><Table.Th>Special Needs</Table.Th><Table.Th>Intake</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iSurvivors.filter(s => s.status === 'checked_in').sort((a, b) => a.lastName.localeCompare(b.lastName)).map(s => {
                  let needs: string[] = [];
                  try { needs = JSON.parse(s.specialNeeds); } catch { /* skip */ }
                  return (
                    <Table.Tr key={s.id}>
                      <Table.Td fw={500}>{s.lastName}, {s.firstName}</Table.Td>
                      <Table.Td>{s.shelterId ? shelterName(s.shelterId) : '-'}</Table.Td>
                      <Table.Td>{s.cotAssignment || '-'}</Table.Td>
                      <Table.Td>{s.phone ? formatPhone(s.phone) : '-'}</Table.Td>
                      <Table.Td>
                        {needs.length > 0
                          ? needs.map(n => getSpecialNeedLabel(n)).join(', ')
                          : '-'}
                      </Table.Td>
                      <Table.Td>{s.intakeTimestamp ? new Date(s.intakeTimestamp).toLocaleDateString() : '-'}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table></div>
          </>
        )}

        {reportType === 'capacity' && (
          <>
            <Text fw={700} mb="xs">Capacity Report</Text>
            <div className="report-scroll"><Table striped withTableBorder stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Shelter</Table.Th><Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Population</Table.Th><Table.Th ta="right">Capacity</Table.Th>
                  <Table.Th ta="right">Utilization</Table.Th><Table.Th ta="right">Available</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iShelters.map(s => {
                  const pct = s.capacity > 0 ? ((s.currentPopulation / s.capacity) * 100).toFixed(1) : '0';
                  const avail = Math.max(s.capacity - s.currentPopulation, 0);
                  return (
                    <Table.Tr key={s.id}>
                      <Table.Td fw={500}>{s.name}</Table.Td>
                      <Table.Td><Badge size="xs">{s.status.replace(/_/g, ' ')}</Badge></Table.Td>
                      <Table.Td ta="right">{s.currentPopulation}</Table.Td>
                      <Table.Td ta="right">{s.capacity}</Table.Td>
                      <Table.Td ta="right">{pct}%</Table.Td>
                      <Table.Td ta="right" fw={500}>{avail}</Table.Td>
                    </Table.Tr>
                  );
                })}
                <Table.Tr>
                  <Table.Td fw={700}>Total</Table.Td>
                  <Table.Td></Table.Td>
                  <Table.Td ta="right" fw={700}>{totalPopulation}</Table.Td>
                  <Table.Td ta="right" fw={700}>{totalCapacity}</Table.Td>
                  <Table.Td ta="right" fw={700}>{totalCapacity > 0 ? ((totalPopulation / totalCapacity) * 100).toFixed(1) : 0}%</Table.Td>
                  <Table.Td ta="right" fw={700}>{Math.max(totalCapacity - totalPopulation, 0)}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table></div>
          </>
        )}

        {reportType === 'supply_inventory' && (
          <>
            <Text fw={700} mb="xs">Supply Inventory</Text>
            <div className="report-scroll"><Table striped withTableBorder stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Shelter</Table.Th><Table.Th>Item</Table.Th><Table.Th>Category</Table.Th>
                  <Table.Th ta="right">Qty</Table.Th><Table.Th>Unit</Table.Th>
                  <Table.Th ta="right">Reorder At</Table.Th><Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iSupplies.sort((a, b) => a.itemName.localeCompare(b.itemName)).map(s => (
                  <Table.Tr key={s.id}>
                    <Table.Td>{s.shelterId ? shelterName(s.shelterId) : '-'}</Table.Td>
                    <Table.Td fw={500}>{s.itemName}</Table.Td>
                    <Table.Td>{s.category}</Table.Td>
                    <Table.Td ta="right">{s.quantity}</Table.Td>
                    <Table.Td>{s.unit}</Table.Td>
                    <Table.Td ta="right">{s.reorderThreshold}</Table.Td>
                    <Table.Td>
                      {s.quantity <= s.reorderThreshold
                        ? <Badge size="xs" color="red">Low</Badge>
                        : <Badge size="xs" color="green">OK</Badge>
                      }
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table></div>
          </>
        )}

        {reportType === 'medical_summary' && (
          <>
            <Text fw={700} mb="xs">Medical Log Summary</Text>
            <Divider mb="sm" />
            <SimpleGrid cols={3} mb="md">
              <div><Text size="xs" fw={700}>Total Entries</Text><Text fw={600}>{iMedical.length}</Text></div>
              <div><Text size="xs" fw={700}>Follow-ups Needed</Text><Text fw={600}>{iMedical.filter(m => m.followUpNeeded).length}</Text></div>
              <div><Text size="xs" fw={700}>Medications Administered</Text><Text fw={600}>{iMedical.filter(m => m.entryType === 'medication').length}</Text></div>
            </SimpleGrid>
            <div className="report-scroll"><Table striped withTableBorder stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Survivor</Table.Th><Table.Th>Type</Table.Th><Table.Th>Description</Table.Th>
                  <Table.Th>By</Table.Th><Table.Th>Date</Table.Th><Table.Th>Follow-up</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iMedical.sort((a, b) => (b.id || 0) - (a.id || 0)).map(m => (
                  <Table.Tr key={m.id}>
                    <Table.Td>{m.survivorId ? survivorFullName(m.survivorId) : '-'}</Table.Td>
                    <Table.Td><Badge size="xs" variant="light">{m.entryType}</Badge></Table.Td>
                    <Table.Td maw={250} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.description || m.medicationName || '-'}
                    </Table.Td>
                    <Table.Td>{m.administeredBy || '-'}</Table.Td>
                    <Table.Td>{m.timestamp ? new Date(m.timestamp).toLocaleDateString() : '-'}</Table.Td>
                    <Table.Td>{m.followUpNeeded ? <Badge size="xs" color="orange">Yes</Badge> : '-'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table></div>
          </>
        )}

        {reportType === 'incident_log' && (
          <>
            <Text fw={700} mb="xs">Incident Log</Text>
            <div className="report-scroll"><Table striped withTableBorder stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Shelter</Table.Th><Table.Th>Type</Table.Th><Table.Th>Severity</Table.Th>
                  <Table.Th>Description</Table.Th><Table.Th>Reported By</Table.Th>
                  <Table.Th>Date</Table.Th><Table.Th>Resolved</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iIncidents.sort((a, b) => (b.id || 0) - (a.id || 0)).map(i => (
                  <Table.Tr key={i.id}>
                    <Table.Td>{i.shelterId ? shelterName(i.shelterId) : '-'}</Table.Td>
                    <Table.Td><Badge size="xs" variant="light">{reportTypeLabel(i.reportType)}</Badge></Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={
                        i.severity === 'critical' ? 'red' :
                        i.severity === 'high' ? 'orange' :
                        i.severity === 'medium' ? 'yellow' : 'blue'
                      }>{i.severity}</Badge>
                    </Table.Td>
                    <Table.Td maw={200} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i.description}
                    </Table.Td>
                    <Table.Td>{i.reportedBy || '-'}</Table.Td>
                    <Table.Td>{i.reportedAt ? new Date(i.reportedAt).toLocaleDateString() : '-'}</Table.Td>
                    <Table.Td>{i.resolvedAt ? <Badge size="xs" color="green">Yes</Badge> : <Badge size="xs" color="red">No</Badge>}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table></div>

            <Text size="xs" c="dimmed" mt="lg">
              This report documents all shelter incidents for the active incident period.
              {iIncidents.filter(i => !i.resolvedAt).length} incident{iIncidents.filter(i => !i.resolvedAt).length !== 1 ? 's' : ''} remain unresolved.
            </Text>
          </>
        )}
      </Paper>
    </Stack>
  );
}
